import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Alert,
  Snackbar,
  CircularProgress,
  Chip,
  Tooltip,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import {
  evoConferenceService,
  evoWebhookConfigService,
  EvoConferenceRow,
  EvoConferenceGrantLineResult,
  EvoConferenceReconcileResult,
  EvoConferenceStampSaleDatesResult,
  EvoConferenceDuplicatePendingEvoSaleIdsResult,
  EvoWebhookConfig,
} from '../services/firebaseFunctions';
import { useAuth } from '../contexts/AuthContext';

/** Tamanho de cada lote na chamada `grantFromCsv` (evita timeout em CSVs grandes). */
const GRANT_CSV_BATCH_LINE_COUNT = 250;

function splitConferenceCsvForGrantBatches(csvText: string, batchLineCount: number): string[] {
  const lines = csvText.split(/\r?\n/);
  while (lines.length > 0 && lines[lines.length - 1] === '') {
    lines.pop();
  }
  if (lines.length < 2) {
    return [];
  }
  const header = lines[0];
  const dataLines = lines.slice(1);
  const batches: string[] = [];
  for (let i = 0; i < dataLines.length; i += batchLineCount) {
    batches.push([header, ...dataLines.slice(i, i + batchLineCount)].join('\n'));
  }
  return batches;
}

function escapeCsvCell(value: string | number | boolean): string {
  const s = String(value);
  if (/[",\n\r]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

/**
 * CPF/documento só com dígitos: sem aspas o Excel abre o CSV como número e some zeros à esquerda.
 * Tab após a aspas força interpretação como texto no Excel; o import do grant (strip \\D) ignora o tab.
 */
function escapeCsvSpreadsheetNumericId(value: string | number | boolean): string {
  const s = String(value ?? '');
  if (/^\d+$/.test(s) && s.length > 0) {
    const withTab = `\t${s}`;
    return `"${withTab.replace(/"/g, '""')}"`;
  }
  return escapeCsvCell(value);
}

function buildConferenceCsv(rows: EvoConferenceRow[]): string {
  const headers = [
    'saleDate',
    'idSale',
    'idMember',
    'memberDocument',
    'cpfNormalized',
    'hasReedyItems',
    'hasValidPayment',
    'includedInReport',
    'hasPendingEntitlement',
    'hasPendingReceivable',
    'hasPendingForSaleId',
    'hasPending',
    'hasAccount',
    'userId',
  ];
  const lines = [headers.join(',')];
  for (const r of rows) {
    const hasPending = r.hasPendingEntitlement || r.hasPendingReceivable || r.hasPendingForSaleId;
    lines.push(
      [
        escapeCsvCell(r.saleDate),
        escapeCsvCell(r.idSale),
        escapeCsvCell(r.idMember),
        escapeCsvSpreadsheetNumericId(r.memberDocument),
        escapeCsvSpreadsheetNumericId(r.cpfNormalized),
        escapeCsvCell(r.hasReedyItems),
        escapeCsvCell(r.hasValidPayment),
        escapeCsvCell(r.includedInReport),
        escapeCsvCell(r.hasPendingEntitlement),
        escapeCsvCell(r.hasPendingReceivable),
        escapeCsvCell(r.hasPendingForSaleId),
        escapeCsvCell(hasPending),
        escapeCsvCell(r.hasAccount),
        escapeCsvCell(r.userId),
      ].join(',')
    );
  }
  return lines.join('\n');
}

const EvoWebhookConferencePage: React.FC = () => {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const canManage = hasPermission('conferencia_evo', 'editar');
  const { id } = useParams<{ id: string }>();
  const [config, setConfig] = useState<EvoWebhookConfig | null>(null);
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [dateStart, setDateStart] = useState('');
  const [dateEnd, setDateEnd] = useState('');
  const [rows, setRows] = useState<EvoConferenceRow[]>([]);
  const [listLoading, setListLoading] = useState(false);
  const [meta, setMeta] = useState<{
    totalSalesFromEvo: number;
    rowsInReport: number;
    evoListChunks: number;
  } | null>(null);
  const [grantLoading, setGrantLoading] = useState(false);
  const [reconcileLoading, setReconcileLoading] = useState(false);
  const [reconcileSummary, setReconcileSummary] = useState<EvoConferenceReconcileResult | null>(null);
  const [stampLoading, setStampLoading] = useState(false);
  const [stampSummary, setStampSummary] = useState<EvoConferenceStampSaleDatesResult | null>(null);
  const [duplicateLoading, setDuplicateLoading] = useState(false);
  const [deleteDuplicateLoading, setDeleteDuplicateLoading] = useState(false);
  const [duplicateResult, setDuplicateResult] = useState<EvoConferenceDuplicatePendingEvoSaleIdsResult | null>(null);
  const [duplicateDeleteSummary, setDuplicateDeleteSummary] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'warning',
  });

  const loadConfig = useCallback(async () => {
    if (!id) return;
    setLoadingConfig(true);
    try {
      const cfg = await evoWebhookConfigService.getById(id);
      setConfig(cfg);
    } catch (e) {
      setConfig(null);
      setSnackbar({
        open: true,
        message: e instanceof Error ? e.message : 'Erro ao carregar config',
        severity: 'error',
      });
    } finally {
      setLoadingConfig(false);
    }
  }, [id]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const handleList = async () => {
    if (!id || !dateStart || !dateEnd) {
      setSnackbar({ open: true, message: 'Informe data inicial e final', severity: 'error' });
      return;
    }
    setListLoading(true);
    try {
      const data = await evoConferenceService.listSales({
        evoWebhookConfigId: id,
        dateSaleStart: dateStart,
        dateSaleEnd: dateEnd,
      });
      setRows(data.rows);
      setMeta({
        totalSalesFromEvo: data.totalSalesFromEvo,
        rowsInReport: data.rowsInReport,
        evoListChunks: data.evoListChunks,
      });
      setSnackbar({
        open: true,
        message: `${data.rows.length} linha(s) no relatório (pagamento válido + itens Reedy; removidas na EVO excluídas).`,
        severity: 'success',
      });
    } catch (e) {
      setRows([]);
      setMeta(null);
      setSnackbar({
        open: true,
        message: e instanceof Error ? e.message : 'Erro ao listar vendas',
        severity: 'error',
      });
    } finally {
      setListLoading(false);
    }
  };

  const handleDownloadCsv = () => {
    if (rows.length === 0) return;
    const csv = buildConferenceCsv(rows);
    const blob = new Blob(['\ufeff', csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `evo-conference-${id}-${dateStart}-${dateEnd}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleGrantFile = async (file: File | null) => {
    if (!file || !id) return;
    setGrantLoading(true);
    try {
      const csvText = await file.text();
      const batches = splitConferenceCsvForGrantBatches(csvText, GRANT_CSV_BATCH_LINE_COUNT);
      if (batches.length === 0) {
        setSnackbar({
          open: true,
          message: 'CSV inválido ou sem linhas de dados após o cabeçalho.',
          severity: 'error',
        });
        return;
      }

      const allResults: EvoConferenceGrantLineResult[] = [];
      let processed = 0;
      let grantedOrSkippedOk = 0;

      for (let i = 0; i < batches.length; i++) {
        const data = await evoConferenceService.grantFromCsv({
          evoWebhookConfigId: id,
          csvText: batches[i],
        });
        allResults.push(...data.results);
        processed += data.processed;
        grantedOrSkippedOk += data.grantedOrSkippedOk;
      }

      const failed = allResults.filter((r) => !r.ok);
      const batchHint =
        batches.length > 1 ? ` (${batches.length} lotes de até ${GRANT_CSV_BATCH_LINE_COUNT} linhas).` : '';
      setSnackbar({
        open: true,
        message:
          failed.length === 0
            ? `Concluído: ${grantedOrSkippedOk} linha(s) OK de ${processed}.${batchHint}`
            : `Processadas ${processed}: ${failed.length} erro(s). Verifique o console.${batchHint}`,
        severity: failed.length ? 'error' : 'success',
      });
      if (failed.length) {
        console.warn('EVO conference grant failures', failed);
      }
    } catch (e) {
      setSnackbar({
        open: true,
        message: e instanceof Error ? e.message : 'Erro ao importar CSV',
        severity: 'error',
      });
    } finally {
      setGrantLoading(false);
    }
  };

  const handleReconcileFile = async (file: File | null) => {
    if (!file || !id) return;
    setReconcileLoading(true);
    setReconcileSummary(null);
    try {
      const csvText = await file.text();
      const data = await evoConferenceService.reconcileFromCsv({ evoWebhookConfigId: id, csvText });
      setReconcileSummary(data);
      const notOk = data.perCpf.filter((p) => !p.ok).length;
      setSnackbar({
        open: true,
        message:
          notOk === 0
            ? `Reconciliação: ${data.cpfOk} CPF(s) OK de ${data.cpfProcessed}. Linhas ignoradas: ${data.skippedRows.length}.`
            : `Reconciliação: ${data.cpfOk} OK, ${notOk} CPF(s) com pendências. Ver tabela abaixo.`,
        severity: notOk === 0 ? 'success' : 'warning',
      });
      if (data.skippedRows.length) {
        console.warn('EVO reconcile skipped rows', data.skippedRows);
      }
      if (notOk) {
        console.warn('EVO reconcile CPFs com avisos', data.perCpf.filter((p) => !p.ok));
      }
    } catch (e) {
      setReconcileSummary(null);
      setSnackbar({
        open: true,
        message: e instanceof Error ? e.message : 'Erro na reconciliação',
        severity: 'error',
      });
    } finally {
      setReconcileLoading(false);
    }
  };

  const handleStampSaleDatesFile = async (file: File | null) => {
    if (!file || !id) return;
    setStampLoading(true);
    setStampSummary(null);
    try {
      const csvText = await file.text();
      const data = await evoConferenceService.stampSaleDatesFromCsv({ evoWebhookConfigId: id, csvText });
      setStampSummary(data);
      const { summary } = data;
      setSnackbar({
        open: true,
        message: `Datas de venda: ${summary.updated} linha(s) atualizaram pending(s); não encontrado: ${summary.notFound}; saleDate inválida: ${summary.invalidSaleDate}.`,
        severity: summary.invalidSaleDate > 0 || summary.notFound > 0 ? 'warning' : 'success',
      });
    } catch (e) {
      setStampSummary(null);
      setSnackbar({
        open: true,
        message: e instanceof Error ? e.message : 'Erro ao aplicar datas de venda',
        severity: 'error',
      });
    } finally {
      setStampLoading(false);
    }
  };

  const handleListDuplicateEvoSaleIds = async () => {
    if (!id) return;
    setDuplicateLoading(true);
    setDuplicateResult(null);
    try {
      const data = await evoConferenceService.listDuplicatePendingEvoSaleIds({ evoWebhookConfigId: id });
      setDuplicateResult(data);
      setSnackbar({
        open: true,
        message:
          data.duplicateGroupCount === 0
            ? 'Nenhum evoSaleId duplicado em pending_entitlements para esta empresa.'
            : `${data.duplicateGroupCount} grupo(s) duplicado(s), ${data.docsInDuplicateGroups} documento(s). Use os IDs na coleção pending_entitlements para editar manualmente.`,
        severity: data.duplicateGroupCount === 0 ? 'success' : 'warning',
      });
    } catch (e) {
      setDuplicateResult(null);
      setSnackbar({
        open: true,
        message: e instanceof Error ? e.message : 'Erro ao verificar duplicados',
        severity: 'error',
      });
    } finally {
      setDuplicateLoading(false);
    }
  };

  const handleDeleteDuplicateEvoSaleIds = async () => {
    if (!id) return;
    const confirmed = window.confirm(
      'Apagar documentos duplicados em pending_entitlements (mesmo evoSaleId)?\n\n' +
        'Regra: mantém-se o pending do título com menor ordem na lista da empresa (campo order em lists.items). ' +
        'Os pendings cujo título está mais à frente na lista serão excluídos. ' +
        'Se forem o mesmo título, mantém-se o documento mais antigo (createdAt).\n\n' +
        'Esta ação não pode ser desfeita.'
    );
    if (!confirmed) return;
    setDeleteDuplicateLoading(true);
    setDuplicateDeleteSummary(null);
    try {
      const del = await evoConferenceService.deleteDuplicatePendingEvoSaleIds({ evoWebhookConfigId: id });
      const refresh = await evoConferenceService.listDuplicatePendingEvoSaleIds({ evoWebhookConfigId: id });
      setDuplicateResult(refresh);
      const listHint = del.listId ? `Lista: ${del.listId}.` : 'Empresa sem listId — ordem da lista não aplicável; usado só createdAt entre duplicatas.';
      setDuplicateDeleteSummary(
        `${listHint} Grupos ajustados: ${del.groupsAdjusted}. Documentos apagados: ${del.deletedCount}.`
      );
      setSnackbar({
        open: true,
        message:
          del.deletedCount === 0
            ? 'Nenhum documento duplicado para apagar (execute a verificação antes se necessário).'
            : `${del.deletedCount} pending(s) apagado(s). Verificação atualizada abaixo.`,
        severity: 'success',
      });
    } catch (e) {
      setSnackbar({
        open: true,
        message: e instanceof Error ? e.message : 'Erro ao apagar duplicados',
        severity: 'error',
      });
    } finally {
      setDeleteDuplicateLoading(false);
    }
  };

  if (loadingConfig) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!config) {
    return (
      <Box p={3}>
        <Alert severity="error">Cadastro não encontrado</Alert>
      </Box>
    );
  }

  const canRun = Boolean(config.dns && config.token && config.companyId);

  return (
    <Box p={3}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <IconButton onClick={() => navigate(`/evo-webhooks/${id}`)} color="primary" aria-label="Voltar">
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4" component="h1">
          Conferência — {config.name}
        </Typography>
      </Box>

      {!canRun && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Configure credenciais e empresa neste cadastro EVO antes de usar a conferência.
        </Alert>
      )}

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Período (registro da venda na EVO, yyyy-mm-dd)
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', mt: 2 }}>
          <TextField
            label="Data início"
            type="date"
            value={dateStart}
            onChange={(e) => setDateStart(e.target.value)}
            InputLabelProps={{ shrink: true }}
            disabled={!canRun}
          />
          <TextField
            label="Data fim"
            type="date"
            value={dateEnd}
            onChange={(e) => setDateEnd(e.target.value)}
            InputLabelProps={{ shrink: true }}
            disabled={!canRun}
          />
          <Button variant="contained" onClick={handleList} disabled={!canRun || listLoading}>
            {listLoading ? <CircularProgress size={24} color="inherit" /> : 'Gerar relatório'}
          </Button>
          <Button variant="outlined" onClick={handleDownloadCsv} disabled={rows.length === 0}>
            Baixar CSV
          </Button>
        </Box>
        {meta && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Vendas únicas na EVO (período): {meta.totalSalesFromEvo}. Consultas à API por intervalo de até 7 dias:{' '}
            {meta.evoListChunks}. Linhas no relatório: {meta.rowsInReport}.
          </Typography>
        )}
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Conceder a partir de CSV editado
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Edite o CSV exportado (remova linhas indesejadas). São obrigatórias as colunas <code>idSale</code> e{' '}
          <code>cpfNormalized</code> (como no export). A coluna <code>saleDate</code> (data da venda) é opcional; quando
          presente e válida, é gravada no <code>pending_entitlement</code> criado. Não há nova chamada à API EVO: a concessão
          usa só os dados do CSV.
        </Typography>
        <Tooltip title={canManage ? '' : 'Você não tem permissão de edição no módulo Conferência EVO.'}>
          <span>
            <Button variant="contained" component="label" disabled={!canManage || !canRun || grantLoading}>
              {grantLoading ? <CircularProgress size={24} color="inherit" /> : 'Enviar CSV'}
              <input
                type="file"
                accept=".csv,text/csv"
                hidden
                onChange={(e) => {
                  const f = e.target.files?.[0] ?? null;
                  e.target.value = '';
                  handleGrantFile(f);
                }}
              />
            </Button>
          </span>
        </Tooltip>
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Reconciliar por CPF (só pending)
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          CSV com <code>idSale</code> e <code>cpfNormalized</code> (como no export). Inclua <code>saleDate</code> para gravar a
          data da venda em cada pending criado. As linhas são agrupadas por CPF: o esperado é{' '}
          <strong>um idSale distinto por linha</strong> (duplicatas no mesmo CPF viram uma só venda). Para cada CPF, comparamos a
          quantidade de <code>pending_entitlements</code> com essa lista; tratamos <code>pendingReceivable</code> sem grant
          duplicado quando já existe pending com o mesmo <code>evoSaleId</code>. CPF com usuário cadastrado na base é ignorado com
          aviso (tratamento manual). Não remove pendings a mais automaticamente.
        </Typography>
        <Tooltip title={canManage ? '' : 'Você não tem permissão de edição no módulo Conferência EVO.'}>
          <span>
            <Button variant="contained" color="secondary" component="label" disabled={!canManage || !canRun || reconcileLoading}>
              {reconcileLoading ? <CircularProgress size={24} color="inherit" /> : 'Enviar CSV de reconciliação'}
              <input
                type="file"
                accept=".csv,text/csv"
                hidden
                onChange={(e) => {
                  const f = e.target.files?.[0] ?? null;
                  e.target.value = '';
                  handleReconcileFile(f);
                }}
              />
            </Button>
          </span>
        </Tooltip>
        {reconcileSummary && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Resumo: {reconcileSummary.cpfOk} OK de {reconcileSummary.cpfProcessed} CPF(s). Ignoradas:{' '}
              {reconcileSummary.skippedRows.length} linha(s).
            </Typography>
            <TableContainer sx={{ maxHeight: 360 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>CPF</TableCell>
                    <TableCell>Esperado</TableCell>
                    <TableCell>Antes</TableCell>
                    <TableCell>Depois</TableCell>
                    <TableCell>OK</TableCell>
                    <TableCell>Avisos / ações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reconcileSummary.perCpf.map((p) => (
                    <TableRow key={p.cpf}>
                      <TableCell>{p.cpf}</TableCell>
                      <TableCell>{p.expected}</TableCell>
                      <TableCell>{p.actualBefore}</TableCell>
                      <TableCell>{p.actualAfter}</TableCell>
                      <TableCell>
                        <Chip size="small" label={p.ok ? 'sim' : 'não'} color={p.ok ? 'success' : 'warning'} />
                      </TableCell>
                      <TableCell sx={{ maxWidth: 420, whiteSpace: 'pre-wrap', fontSize: 12 }}>
                        {[...p.warnings, ...p.actions].join('\n') || '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Aplicar data de venda nos pendings (CSV exportado)
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Envie o mesmo CSV exportado desta conferência (com colunas <code>saleDate</code>, <code>idSale</code> e{' '}
          <code>cpfNormalized</code>). Para cada linha válida, atualizamos <code>saleDate</code> nos documentos em{' '}
          <code>pending_entitlements</code> que casam <strong>empresa desta config</strong>, CPF e <code>evoSaleId</code>. A
          célula <code>saleDate</code> deve ser uma data reconhecível (ex. yyyy-mm-dd). Linhas com data vazia ou inválida são
          ignoradas para essa coluna.
        </Typography>
        <Tooltip title={canManage ? '' : 'Você não tem permissão de edição no módulo Conferência EVO.'}>
          <span>
            <Button variant="outlined" component="label" disabled={!canManage || !canRun || stampLoading}>
              {stampLoading ? <CircularProgress size={24} color="inherit" /> : 'Enviar CSV para atualizar saleDate'}
              <input
                type="file"
                accept=".csv,text/csv"
                hidden
                onChange={(e) => {
                  const f = e.target.files?.[0] ?? null;
                  e.target.value = '';
                  handleStampSaleDatesFile(f);
                }}
              />
            </Button>
          </span>
        </Tooltip>
        {stampSummary && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Resumo: {stampSummary.summary.updated} linha(s) com pending atualizado; não encontrado: {stampSummary.summary.notFound};{' '}
              <code>saleDate</code> inválida: {stampSummary.summary.invalidSaleDate} (de {stampSummary.summary.rowsProcessed}{' '}
              linhas processadas).
            </Typography>
            <TableContainer sx={{ maxHeight: 280 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>idSale</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Docs atualizados</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {stampSummary.lines.map((line, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{line.idSale}</TableCell>
                      <TableCell>{line.status}</TableCell>
                      <TableCell>{line.pendingDocsUpdated ?? '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Duplicados de <code>evoSaleId</code> em <code>pending_entitlements</code>
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Verifica todos os documentos da coleção <code>pending_entitlements</code> da <strong>empresa desta config</strong>.
          Documentos sem <code>evoSaleId</code> válido são ignorados na análise. O mesmo ID normalizado em dois ou mais
          documentos aparece como duplicata — use o ID do documento para localizar e corrigir no Firestore, ou use o botão
          de exclusão automática (regra da lista abaixo).
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <Button variant="outlined" onClick={handleListDuplicateEvoSaleIds} disabled={!canRun || duplicateLoading}>
            {duplicateLoading ? <CircularProgress size={24} color="inherit" /> : 'Verificar duplicados de EvoSaleId'}
          </Button>
          <Tooltip title={canManage ? '' : 'Você não tem permissão de edição no módulo Conferência EVO.'}>
            <span>
              <Button
                variant="outlined"
                color="error"
                onClick={handleDeleteDuplicateEvoSaleIds}
                disabled={!canManage || !canRun || duplicateLoading || deleteDuplicateLoading}
              >
                {deleteDuplicateLoading ? <CircularProgress size={24} color="inherit" /> : 'Apagar duplicados (regra da lista)'}
              </Button>
            </span>
          </Tooltip>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
          <strong>Apagar duplicados:</strong> usa a lista vinculada à empresa (<code>companies.listId</code> →{' '}
          <code>lists.items[].order</code>). Entre pendings com o mesmo <code>evoSaleId</code>, remove os do título com{' '}
          <code>order</code> maior (mais à frente na lista). Título fora da lista conta como último. Mesmo título: mantém o
          pending mais antigo.
        </Typography>
        {duplicateDeleteSummary && (
          <Alert severity="info" sx={{ mt: 2 }} onClose={() => setDuplicateDeleteSummary(null)}>
            {duplicateDeleteSummary}
          </Alert>
        )}
        {duplicateResult && (
          <Box sx={{ mt: 2 }}>
            {duplicateResult.duplicateGroupCount === 0 ? (
              <Alert severity="success">
                Nenhum duplicado encontrado. Total de pendings da empresa: {duplicateResult.totalPendingDocs}; com{' '}
                <code>evoSaleId</code> válido: {duplicateResult.docsWithValidEvoSaleId}
                {duplicateResult.docsWithoutValidEvoSaleId > 0
                  ? `; sem evoSaleId válido (ignorados): ${duplicateResult.docsWithoutValidEvoSaleId}.`
                  : '.'}
              </Alert>
            ) : (
              <>
                <Alert severity="warning" sx={{ mb: 2 }}>
                  {duplicateResult.duplicateGroupCount} grupo(s), {duplicateResult.docsInDuplicateGroups} documento(s) em
                  duplicata. Pendings na empresa: {duplicateResult.totalPendingDocs}; com <code>evoSaleId</code> válido:{' '}
                  {duplicateResult.docsWithValidEvoSaleId}.
                </Alert>
                {duplicateResult.duplicateGroups.map((g) => (
                  <Box key={g.evoSaleId} sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" sx={{ mb: 1 }}>
                      <code>evoSaleId</code> = {g.evoSaleId} ({g.docs.length} documentos)
                    </Typography>
                    <TableContainer sx={{ maxHeight: 320 }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell>Doc ID</TableCell>
                            <TableCell>CPF</TableCell>
                            <TableCell>titleId</TableCell>
                            <TableCell>Título</TableCell>
                            <TableCell>source</TableCell>
                            <TableCell>saleDate</TableCell>
                            <TableCell>processedAt</TableCell>
                            <TableCell>createdAt</TableCell>
                            <TableCell>errorMessage</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {g.docs.map((d) => (
                            <TableRow key={d.id}>
                              <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{d.id}</TableCell>
                              <TableCell>{d.cpf}</TableCell>
                              <TableCell sx={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {d.titleId}
                              </TableCell>
                              <TableCell sx={{ maxWidth: 180, whiteSpace: 'pre-wrap', fontSize: 12 }}>
                                {d.titleName ?? '—'}
                              </TableCell>
                              <TableCell>{d.source ?? '—'}</TableCell>
                              <TableCell sx={{ fontSize: 12 }}>{d.saleDate ?? '—'}</TableCell>
                              <TableCell sx={{ fontSize: 12 }}>{d.processedAt ?? '—'}</TableCell>
                              <TableCell sx={{ fontSize: 12 }}>{d.createdAt ?? '—'}</TableCell>
                              <TableCell sx={{ maxWidth: 200, whiteSpace: 'pre-wrap', fontSize: 12 }}>
                                {d.errorMessage ?? '—'}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                ))}
              </>
            )}
          </Box>
        )}
      </Paper>

      {rows.length > 0 && (
        <TableContainer component={Paper}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Data venda</TableCell>
                <TableCell>idSale</TableCell>
                <TableCell>idMember</TableCell>
                <TableCell>Documento</TableCell>
                <TableCell>CPF norm.</TableCell>
                <TableCell>Reedy</TableCell>
                <TableCell>Pagamento</TableCell>
                <TableCell>No relatório</TableCell>
                <TableCell>Pending</TableCell>
                <TableCell>Pend. idSale</TableCell>
                <TableCell>Conta</TableCell>
                <TableCell>userId</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={`${r.idSale}-${r.cpfNormalized}`}>
                  <TableCell>{r.saleDate}</TableCell>
                  <TableCell>{r.idSale}</TableCell>
                  <TableCell>{r.idMember}</TableCell>
                  <TableCell>{r.memberDocument}</TableCell>
                  <TableCell>{r.cpfNormalized}</TableCell>
                  <TableCell>
                    <Chip size="small" label={r.hasReedyItems ? 'sim' : 'não'} color={r.hasReedyItems ? 'success' : 'default'} />
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={r.hasValidPayment ? 'sim' : 'não'} color={r.hasValidPayment ? 'success' : 'default'} />
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={r.includedInReport ? 'sim' : 'não'}
                      color={r.includedInReport ? 'primary' : 'default'}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={r.hasPendingEntitlement || r.hasPendingReceivable ? 'sim' : 'não'}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={r.hasPendingForSaleId ? 'sim' : 'não'}
                      color={r.hasPendingForSaleId ? 'warning' : 'default'}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={r.hasAccount ? 'sim' : 'não'} variant="outlined" />
                  </TableCell>
                  <TableCell sx={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.userId}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={8000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EvoWebhookConferencePage;
